import { CheckIcon } from '@heroicons/react/24/solid'
import { useTranslation } from 'react-i18next'

function classNames(...classes) {
  return classes.filter(Boolean).join(' ')
}

// Substeps are laid out as rows of this many rather than one long strip: Prepare has 8
// and Analyze 6, which used to overflow the card and put the last ones out of reach.
const DEFAULT_PER_ROW = 4

function chunk(items, size) {
  const rows = []
  for (let i = 0; i < items.length; i += size) {
    rows.push(items.slice(i, i + size))
  }
  return rows
}

export default function ProjectSubTabs({ tabs, currentTab, onTabClick, currentStep = 0, completedSubsteps = [], perRow = DEFAULT_PER_ROW }) {
  const { t } = useTranslation()

  if (!tabs || tabs.length === 0) return null

  // Use the pre-filtered tabs that already have displayId set
  const visibleTabs = tabs.filter(tab => tab.visible).map((tab) => {
    const isCompleted = completedSubsteps.includes(tab.displayId);
    return {
      ...tab,
      status: isCompleted ? 'complete' : (tab.current ? 'current' : 'upcoming'),
      isCompleted,
      // displayId is already set by getSubtabsForStep()
    };
  })

  if (visibleTabs.length === 0) return null

  const rows = chunk(visibleTabs, Math.max(1, perRow))

  return (
    <div className="mb-1 flex flex-col gap-2">
      <nav aria-label="Sub navigation">
        {rows.map((row, rowIdx) => (
          <ol
            key={`subtab-row-${rowIdx}`}
            role="list"
            className={classNames('flex rounded-md border border-gray-300', rowIdx > 0 && 'mt-2')}
          >
            {row.map((tab, tabIdx) => (
              <li key={tab.name} className="relative flex-1 min-w-0">
                {tab.status === 'complete' ? (
                  <button onClick={() => onTabClick(tab.id)} data-testid={`flow-subtab-${tab.id}`} title={tab.name} className="flex w-full items-center pl-3 pr-4 py-2 text-sm font-medium">
                    <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-iavisus-100">
                      <CheckIcon className="size-4 text-white" />
                    </span>
                    <span className="ml-2 text-xs font-bold text-iavisus-100 truncate">{tab.name}</span>
                  </button>
                ) : tab.status === 'current' ? (
                  <button onClick={() => onTabClick(tab.id)} data-testid={`flow-subtab-${tab.id}`} title={tab.name} aria-current="page" className="flex w-full items-center pl-3 pr-4 py-2 text-sm font-medium">
                    <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-iavisus-900">
                      <span className="text-white text-xs">{tab.displayId}</span>
                    </span>
                    <span className="ml-2 text-xs font-bold text-iavisus-100 truncate">{tab.name}</span>
                  </button>
                ) : (
                  <button onClick={() => onTabClick(tab.id)} data-testid={`flow-subtab-${tab.id}`} title={tab.name} className="group flex w-full items-center">
                    <span className="flex w-full items-center pl-3 pr-4 py-2 text-sm font-medium">
                      <span className="flex size-6 shrink-0 items-center justify-center rounded-full border-2 border-gray-300 group-hover:border-gray-400">
                        <span className="text-gray-500 group-hover:text-iavisus-100 text-xs">{tab.displayId}</span>
                      </span>
                      <span className="ml-2 text-xs font-bold text-gray-500 group-hover:text-iavisus-100 truncate">{tab.name}</span>
                    </span>
                  </button>
                )}

                {/* Arrow separator — never on the last item of a row, so no arrow points into empty space */}
                {tabIdx !== row.length - 1 ? (
                  <div aria-hidden="true" className="absolute right-0 top-0 hidden h-full w-3 md:block">
                    <svg fill="none" viewBox="0 0 22 80" preserveAspectRatio="none" className="size-full text-gray-300">
                      <path
                        d="M0 -2L20 40L0 82"
                        stroke="currentcolor"
                        vectorEffect="non-scaling-stroke"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                ) : null}
              </li>
            ))}
          </ol>
        ))}
      </nav>
    </div>
  )
}
